import React, { useState, createContext, useContext, Component, useRef } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import "./styles.css";
import { LanguageProvider, useLanguage, LanguageContext } from "./i18n";
import LanguageSwitcher from "./components/LanguageSwitcher";
import MediShiftDemo from "./components/MediShiftDemo";

// Error Boundary Component
export class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static contextType = LanguageContext;

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("ErrorBoundary caught:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      const t = this.context?.t ?? ((k) => k);
      return (
        <div className="error-boundary">
          <h1>{t("errorBoundary.title")}</h1>
          <p>{t("errorBoundary.message")}</p>
          <button onClick={() => window.location.reload()}>{t("errorBoundary.refresh")}</button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Toast Notification Component
function Toast({ message, type, onClose }) {
  React.useEffect(() => {
    const timer = setTimeout(onClose, 5000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`toast toast-${type}`} role="alert" aria-live="polite">
      <span>{message}</span>
      <button onClick={onClose} className="toast-close" aria-label="Close notification">&times;</button>
    </div>
  );
}

// Theme context for global theme state
export const ThemeContext = createContext();

export function useTheme() {
  return useContext(ThemeContext);
}

function AppContent() {
  const { t, language } = useLanguage();
  const [theme, setTheme] = useState(() => {
    // Persist theme in localStorage for reloads
    return localStorage.getItem("theme") || "light";
  });
  const [selectedAgentKey, setSelectedAgentKey] = useState("");
  const [activeAgentIndex, setActiveAgentIndex] = useState(0);
  const [isOrderFormOpen, setIsOrderFormOpen] = useState(false);
  const [isAgentMenuOpen, setIsAgentMenuOpen] = useState(false);
  const [formDrafts, setFormDrafts] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toast, setToast] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [expandedFaqs, setExpandedFaqs] = useState({});
  const apiBaseUrl =
    process.env.REACT_APP_API_BASE_URL ||
    (["localhost", "127.0.0.1", "::1"].includes(window.location.hostname)
      ? "http://localhost:5000"
      : "/api");
  const youtubeOrigin = encodeURIComponent(window.location.origin);

  const agentOptions = [
    {
      key: "medishift",
      name: "MediShift",
      icon: "/assets/logos/MediShift.jpg",
      type: "existing",
    },
    {
      key: "frostdesk",
      name: "FrostDesk - AI Powered Parts Research",
      icon: "/assets/logos/FrostDesk_Logo.jpg",
      type: "existing",
    },
    {
      key: "piptronix",
      name: "PipTronix AI",
      icon: "/assets/logos/PipTronix_AI_logo.jpg",
      type: "existing",
    },
    {
      key: "athena",
      name: "Athena",
      icon: "/assets/logos/Athena.jpg",
      type: "existing",
      hidden: language !== "it",
    },
    {
      key: "propose",
      name: t("agentOptions.proposeAgent"),
      icon: "/assets/logos/GenericAgent.svg",
      type: "propose",
    },
  ];

  const agentShowcase = [
    {
      key: "medishift",
      name: "MediShift",
      tagline: t("agentOptions.mediShiftTagline"),
      icon: "/assets/logos/MediShift.jpg",
      kind: "video",
      videoEmbedUrl: language === "it"
        ? `https://www.youtube-nocookie.com/embed/Jq4CEV7aKww?rel=0&modestbranding=1&controls=1&iv_load_policy=3&playsinline=1&autoplay=1&mute=1&enablejsapi=1&loop=1&playlist=Jq4CEV7aKww&origin=${youtubeOrigin}`
        : `https://www.youtube-nocookie.com/embed/GkUdxUEq3cI?rel=0&modestbranding=1&controls=1&iv_load_policy=3&playsinline=1&autoplay=1&mute=1&enablejsapi=1&loop=1&playlist=GkUdxUEq3cI&origin=${youtubeOrigin}`,
      videoExternalUrl: language === "it"
        ? "https://www.youtube.com/watch?v=Jq4CEV7aKww&start=0&autoplay=1&t=0s"
        : "https://www.youtube.com/watch?v=GkUdxUEq3cI&start=0&autoplay=1&t=0s",
    },
    {
      key: "frostdesk",
      name: "FrostDesk - AI Powered Parts Research",
      tagline: t("agentOptions.frostDeskTagline"),
      icon: "/assets/logos/FrostDesk_Logo.jpg",
      kind: "video",
      videoEmbedUrl: language === "it"
        ? `https://www.youtube-nocookie.com/embed/V-5f9bWPpdI?rel=0&modestbranding=1&controls=1&iv_load_policy=3&playsinline=1&autoplay=1&mute=1&enablejsapi=1&loop=1&playlist=V-5f9bWPpdI&origin=${youtubeOrigin}`
        : `https://www.youtube-nocookie.com/embed/hPxpLcJqSy4?rel=0&modestbranding=1&controls=1&iv_load_policy=3&playsinline=1&autoplay=1&mute=1&enablejsapi=1&loop=1&playlist=hPxpLcJqSy4&origin=${youtubeOrigin}`,
      videoExternalUrl: language === "it"
        ? "https://youtu.be/V-5f9bWPpdI"
        : "https://youtu.be/hPxpLcJqSy4",
    },
    {
      key: "athena",
      name: "Athena",
      tagline: language === "it" ? t("agentOptions.athenaTagline") : "",
      icon: "/assets/logos/Athena.jpg",
      kind: "video",
      videoEmbedUrl: `https://www.youtube-nocookie.com/embed/MiLalK94GQ8?rel=0&modestbranding=1&controls=1&iv_load_policy=3&playsinline=1&autoplay=1&mute=1&enablejsapi=1&loop=1&playlist=MiLalK94GQ8&origin=${youtubeOrigin}`,
      videoExternalUrl: "https://youtu.be/MiLalK94GQ8",
      hidden: language !== "it",
    },
    {
      key: "piptronix",
      name: "PipTronix AI",
      tagline: t("agentOptions.pipTronixTagline"),
      icon: "/assets/logos/PipTronix_AI_logo.jpg",
      kind: "video",
      videoEmbedUrl: language === "it"
        ? `https://www.youtube-nocookie.com/embed/hu1xe8NP9a4?rel=0&modestbranding=1&controls=1&iv_load_policy=3&playsinline=1&autoplay=1&mute=1&enablejsapi=1&loop=1&playlist=hu1xe8NP9a4&origin=${youtubeOrigin}`
        : `https://www.youtube-nocookie.com/embed/Qw5nRl_3qeQ?rel=0&modestbranding=1&controls=1&iv_load_policy=3&playsinline=1&autoplay=1&mute=1&enablejsapi=1&loop=1&playlist=Qw5nRl_3qeQ&origin=${youtubeOrigin}`,
      videoExternalUrl: language === "it"
        ? "https://youtu.be/hu1xe8NP9a4"
        : "https://youtu.be/Qw5nRl_3qeQ",
    },
    {
      key: "propose",
      name: t("agentOptions.proposeAgent"),
      tagline: t("agentOptions.proposedTagline"),
      icon: "/assets/logos/GenericAgent.svg",
      kind: "selfhosted-video",
      videoSrc: "/assets/Propose_Agent.mp4",
    },
  ];

  // Filter out hidden agents for display
  const visibleAgentOptions = agentOptions.filter((a) => !a.hidden);
  const visibleAgentShowcase = agentShowcase.filter((a) => !a.hidden);

  const selectedAgent = visibleAgentOptions.find(
    (option) => option.key === selectedAgentKey
  );

  const swipeStartRef = useRef({ x: 0, y: 0, active: false });
  const mediShiftVideoRef = useRef(null);

  const goToAgent = (index) => {
    const count = visibleAgentShowcase.length;
    setActiveAgentIndex(((index % count) + count) % count);
  };

  const goToNextAgent = () => {
    setActiveAgentIndex((prev) => (prev + 1) % visibleAgentShowcase.length);
  };

  const goToPrevAgent = () => {
    setActiveAgentIndex(
      (prev) => (prev - 1 + visibleAgentShowcase.length) % visibleAgentShowcase.length
    );
  };

  React.useEffect(() => {
    if (activeAgentIndex >= visibleAgentShowcase.length) {
      setActiveAgentIndex(Math.max(visibleAgentShowcase.length - 1, 0));
    }
  }, [activeAgentIndex, visibleAgentShowcase.length]);

  // Pause/resume MediShift video when navigating away/back
  React.useEffect(() => {
    const iframe = mediShiftVideoRef.current;
    if (!iframe || !iframe.contentWindow || !iframe.src) return;

    let targetOrigin;
    try {
      targetOrigin = new URL(iframe.src).origin;
    } catch (e) {
      // If the iframe src is not a valid URL, avoid sending any messages.
      return;
    }

    // MediShift is at index 0
    if (activeAgentIndex === 0) {
      iframe.contentWindow.postMessage(
        JSON.stringify({ event: "command", func: "playVideo", args: [] }),
        targetOrigin
      );
    } else {
      iframe.contentWindow.postMessage(
        JSON.stringify({ event: "command", func: "pauseVideo", args: [] }),
        targetOrigin
      );
    }
  }, [activeAgentIndex]);

  const handleCarouselTouchStart = (event) => {
    const touch = event.touches?.[0];
    if (!touch) return;
    swipeStartRef.current = { x: touch.clientX, y: touch.clientY, active: true };
  };

  const handleCarouselTouchEnd = (event) => {
    const start = swipeStartRef.current;
    if (!start.active) return;

    const touch = event.changedTouches?.[0];
    swipeStartRef.current.active = false;
    if (!touch) return;

    const deltaX = touch.clientX - start.x;
    const deltaY = touch.clientY - start.y;

    // Only treat it as a swipe if it's clearly horizontal.
    const isHorizontal = Math.abs(deltaX) > Math.abs(deltaY);
    const thresholdPx = 50;
    if (!isHorizontal || Math.abs(deltaX) < thresholdPx) return;

    if (deltaX < 0) {
      goToNextAgent();
    } else {
      goToPrevAgent();
    }
  };

  const activeFormKey = selectedAgent?.key || "";
  const activeFormValues = formDrafts[activeFormKey] || {};

  // Ensure <body> and <html> always have the correct theme class
  React.useEffect(() => {
    document.body.classList.remove("light-theme", "dark-theme");
    document.body.classList.add(`${theme}-theme`);
    document.documentElement.classList.remove("light-theme", "dark-theme");
    document.documentElement.classList.add(`${theme}-theme`);
  }, [theme]);

  const toggleTheme = () => {
    const newTheme = theme === "light" ? "dark" : "light";
    setTheme(newTheme);
    localStorage.setItem("theme", newTheme);
  };

  const showToast = (message, type = "info") => {
    setToast({ message, type });
  };

  const validateForm = (formValues, agentType) => {
    const errors = {};
    
    if (!formValues.contactName || formValues.contactName.trim().length < 2) {
      errors.contactName = t("validation.nameRequired");
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formValues.contactEmail || !emailRegex.test(formValues.contactEmail)) {
      errors.contactEmail = t("validation.emailRequired");
    }
    
    if (agentType === "propose" && !formValues.proposedAgentName) {
      errors.proposedAgentName = t("validation.proposedNameRequired");
    }
    
    return errors;
  };

  const handleOrderSubmit = async (event, agent) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);
    const formValues = Object.fromEntries(formData.entries());
    
    // Client-side validation
    const errors = validateForm(formValues, agent?.type);
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      showToast(t("validation.fixErrors"), "error");
      return;
    }
    setValidationErrors({});
    setIsSubmitting(true);
    
    let timeoutId = null;
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
      
      const response = await fetch(`${apiBaseUrl}/order`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ agent, formValues }),
        signal: controller.signal,
      });
      
      clearTimeout(timeoutId);

      if (!response.ok) {
        let errorMessage = "Unable to submit right now.";
        try {
          const payload = await response.json();
          if (payload?.error) {
            errorMessage = payload.error;
          }
        } catch (parseError) {
          // ignore JSON parsing errors
        }
        throw new Error(errorMessage);
      }

      form.reset();
      if (agent?.key) {
        setFormDrafts((prev) => ({ ...prev, [agent.key]: {} }));
      }
      showToast(t("order.successMessage"), "success");
      setIsOrderFormOpen(false);
    } catch (error) {
      let userMessage = t("errors.unableToSubmit");
      if (error.name === "AbortError") {
        userMessage = t("errors.timeout");
      } else if (
        typeof error?.message === "string" &&
        (error.message === "Failed to fetch" || error.message.includes("NetworkError"))
      ) {
        userMessage = t("errors.network");
      } else if (typeof error?.message === "string" && error.message) {
        userMessage = error.message;
      }
      showToast(userMessage, "error");
    } finally {
      clearTimeout(timeoutId);
      setIsSubmitting(false);
    }
  };

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    if (!activeFormKey) return;
    setFormDrafts((prev) => ({
      ...prev,
      [activeFormKey]: {
        ...(prev[activeFormKey] || {}),
        [name]: value,
      },
    }));
  };

  const handleFormClear = () => {
    if (!activeFormKey) return;
    setFormDrafts((prev) => ({ ...prev, [activeFormKey]: {} }));
    setValidationErrors({});
  };

  const handleFormClose = () => {
    // Just close the form, keep the draft data
    setIsOrderFormOpen(false);
    setValidationErrors({});
  };

  const handleExternalVideoOpen = (url, agentKey) => {
    // Open YouTube in a new tab starting from the beginning
    window.open(url, "_blank", "noopener,noreferrer");

    // Resume playback in the embedded video after opening the new tab
    if (agentKey === "medishift") {
      const iframe = mediShiftVideoRef.current;
      if (iframe?.contentWindow) {
        const playCommand = JSON.stringify({ event: "command", func: "playVideo", args: [] });
        // Send play command multiple times to ensure it works
        iframe.contentWindow.postMessage(playCommand, "*");
        setTimeout(() => iframe.contentWindow.postMessage(playCommand, "*"), 50);
        setTimeout(() => iframe.contentWindow.postMessage(playCommand, "*"), 150);
        setTimeout(() => iframe.contentWindow.postMessage(playCommand, "*"), 300);
        setTimeout(() => iframe.contentWindow.postMessage(playCommand, "*"), 500);
      }
    }
  };

  const faqItems = t("faq.items");

  const toggleFaq = (index) => {
    setExpandedFaqs((prev) => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  return (
    <ErrorBoundary>
      <ThemeContext.Provider value={{ theme, toggleTheme }}>
        <Router>
          {/* Toast Notification */}
          {toast && (
            <Toast
              message={toast.message}
              type={toast.type}
              onClose={() => setToast(null)}
            />
          )}
          {/* Sticky header bar - add navigation, buttons, or branding here */}
          <div className="sticky-header-bar">
            <div className="sticky-header-left">
              <LanguageSwitcher />
            </div>
            <div className="sticky-header-right">
              <button
                className="theme-toggle-btn"
                onClick={toggleTheme}
                aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
              >
                {theme === "light" ? t("theme.dark") : t("theme.light")}
              </button>
            </div>
          </div>
          <Routes>
            <Route path="/medishift-demo" element={<MediShiftDemo />} />
            <Route
              path="/"
              element={
                <div className="webapp-page">
                  {/* Top Bar: Full-width hero header */}
                  <header className="top-bar top-bar-centered top-bar-fullbleed">
                    <div className="top-bar-inner">
                      <img
                        src="/assets/logos/CodeCatLogoNoName.jpg"
                        alt="CodeCat Logo"
                        className="logo-img hero-logo"
                      />
                      <div className="codecat-heading">CodeCat</div>
                      <h1 className="hero-title">
                        {t("hero.title")}
                      </h1>
                      <div className="hero-subtitle">{t("hero.subtitle")}</div>
                    </div>
                  </header>
                  <div className="webapp-root">
                    {/* AI Agents Section */}
                    <section className="agents-section agents-section-fullbleed">
                      <div className="agents-section-inner">
                        <h2>{t("agents.sectionTitle")}</h2>

                        <div
                          className="agent-carousel"
                          role="region"
                          aria-label={t("agents.sliderLabel")}
                        >
                          <button
                            type="button"
                            className="carousel-arrow carousel-arrow-left"
                            onClick={goToPrevAgent}
                            aria-label={t("agents.prevAgent")}
                          >
                            ‹
                          </button>

                          <div
                            className="carousel-viewport"
                            onTouchStart={handleCarouselTouchStart}
                            onTouchEnd={handleCarouselTouchEnd}
                          >
                            <div
                              className="carousel-track"
                              style={{
                                transform: `translateX(-${activeAgentIndex * 100}%)`,
                              }}
                            >
                              {visibleAgentShowcase.map((agent) => (
                                <div key={agent.key} className="carousel-slide">
                                  <div className="agent-card agent-card-carousel">
                                    <img
                                      src={agent.icon}
                                      alt={`${agent.name} logo`}
                                      className="agent-logo"
                                    />
                                    <div className="agent-name">{agent.name}</div>
                                    {agent.tagline && (
                                      <div className="agent-tagline">{agent.tagline}</div>
                                    )}

                                    {agent.kind === "video" ? (
                                      <>
                                        <div className="agent-video-wrap">
                                          <iframe
                                            ref={agent.key === "medishift" ? mediShiftVideoRef : null}
                                            className="agent-video"
                                            src={agent.videoEmbedUrl}
                                            title={`${agent.name} demo`}
                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                            referrerPolicy="strict-origin-when-cross-origin"
                                            allowFullScreen
                                          />
                                        </div>
                                        <button
                                          type="button"
                                          className="agent-video-link"
                                          onClick={() =>
                                            handleExternalVideoOpen(
                                              agent.videoExternalUrl,
                                              agent.key
                                            )
                                          }
                                        >{t("agents.watchOnYoutube")}</button>
                                      </>
                                    ) : agent.kind === "selfhosted-video" ? (
                                      <div className="agent-video-wrap">
                                        <video
                                          className="agent-video"
                                          src={agent.videoSrc}
                                          autoPlay
                                          muted
                                          loop
                                          playsInline
                                        />
                                      </div>
                                    ) : (
                                      <div className="coming-soon">
                                        {agent.placeholderText}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>

                          <button
                            type="button"
                            className="carousel-arrow carousel-arrow-right"
                            onClick={goToNextAgent}
                            aria-label={t("agents.nextAgent")}
                          >
                            ›
                          </button>
                        </div>

                        <div className="carousel-dots" aria-label="Agent navigation">
                          {visibleAgentShowcase.map((agent, index) => (
                            <button
                              key={agent.key}
                              type="button"
                              className={`carousel-dot ${
                                index === activeAgentIndex ? "active" : ""
                              }`}
                              onClick={() => goToAgent(index)}
                              aria-label={`Show ${agent.name}`}
                              aria-current={index === activeAgentIndex}
                            />
                          ))}
                        </div>

                  <div className="agent-order-section">
                    <h3>{t("order.sectionTitle")}</h3>
                    <div className="agent-dropdown">
                      <details
                        className="agent-dropdown-menu"
                        open={isAgentMenuOpen}
                        onToggle={(event) => setIsAgentMenuOpen(event.currentTarget.open)}
                      >
                        <summary className="agent-dropdown-summary">
                          {selectedAgent ? (
                            <>
                              <img
                                src={selectedAgent.icon}
                                alt={`${selectedAgent.name} icon`}
                                className="agent-dropdown-icon"
                              />
                              <span>{selectedAgent.name}</span>
                            </>
                          ) : (
                            <span>{t("order.selectAgent")}</span>
                          )}
                        </summary>
                        <div className="agent-dropdown-options">
                          {visibleAgentOptions.map((option) => (
                            <button
                              key={option.key}
                              type="button"
                              className="agent-dropdown-option"
                              onClick={() => {
                                setSelectedAgentKey(option.key);
                                setIsOrderFormOpen(true);
                                setIsAgentMenuOpen(false);
                              }}
                            >
                              <img
                                src={option.icon}
                                alt={`${option.name} icon`}
                                className="agent-dropdown-icon"
                              />
                              <span>{option.name}</span>
                            </button>
                          ))}
                        </div>
                      </details>
                    </div>

                    {selectedAgent && isOrderFormOpen && (
                      <div className="agent-order-card">
                        <div className="agent-order-header">
                          <img
                            src={selectedAgent.icon}
                            alt={`${selectedAgent.name} icon`}
                            className="agent-order-icon"
                          />
                          <div>
                            <div className="agent-order-title">
                              {selectedAgent.type === "propose"
                                ? t("order.proposeTitle")
                                : t("order.orderTitle")}
                            </div>
                            <div className="agent-order-name">
                              {selectedAgent.name}
                            </div>
                          </div>
                          <button
                            type="button"
                            className="agent-order-submit"
                            style={{ marginLeft: "auto" }}
                            onClick={handleFormClose}
                          >
                            {t("order.close")}
                          </button>
                        </div>

                        <form
                          className="agent-order-form"
                          onSubmit={(event) => handleOrderSubmit(event, selectedAgent)}
                          onChange={handleFormChange}
                        >
                          {/* Honeypot field for spam protection - hidden from users */}
                          <input
                            type="text"
                            name="website"
                            className="honeypot-field"
                            tabIndex="-1"
                            autoComplete="off"
                            aria-hidden="true"
                          />
                          {selectedAgent.type === "propose" ? (
                            <>
                              <div className="form-grid">
                                <label>
                                  {t("proposeForm.proposedAgentName")}
                                  <input
                                    name="proposedAgentName"
                                    type="text"
                                    placeholder={t("proposeForm.proposedAgentNamePlaceholder")}
                                    value={activeFormValues.proposedAgentName || ""}
                                    onChange={handleFormChange}
                                    aria-required="true"
                                    className={validationErrors.proposedAgentName ? "input-error" : ""}
                                  />
                                  {validationErrors.proposedAgentName && (
                                    <span className="error-text">{validationErrors.proposedAgentName}</span>
                                  )}
                                </label>
                                <label>
                                  {t("proposeForm.industryDomain")}
                                  <input
                                    name="industryDomain"
                                    type="text"
                                    placeholder={t("proposeForm.industryDomainPlaceholder")}
                                    value={activeFormValues.industryDomain || ""}
                                    onChange={handleFormChange}
                                  />
                                </label>
                                <label>
                                  {t("proposeForm.contactName")}
                                  <input
                                    name="contactName"
                                    type="text"
                                    placeholder={t("proposeForm.contactNamePlaceholder")}
                                    value={activeFormValues.contactName || ""}
                                    onChange={handleFormChange}
                                    aria-required="true"
                                    className={validationErrors.contactName ? "input-error" : ""}
                                  />
                                  {validationErrors.contactName && (
                                    <span className="error-text">{validationErrors.contactName}</span>
                                  )}
                                </label>
                                <label>
                                  {t("proposeForm.contactEmail")}
                                  <input
                                    name="contactEmail"
                                    type="email"
                                    placeholder={t("proposeForm.contactEmailPlaceholder")}
                                    value={activeFormValues.contactEmail || ""}
                                    onChange={handleFormChange}
                                    aria-required="true"
                                    className={validationErrors.contactEmail ? "input-error" : ""}
                                  />
                                  {validationErrors.contactEmail && (
                                    <span className="error-text">{validationErrors.contactEmail}</span>
                                  )}
                                </label>
                                <label>
                                  {t("proposeForm.company")}
                                  <input
                                    name="company"
                                    type="text"
                                    placeholder={t("proposeForm.companyPlaceholder")}
                                    value={activeFormValues.company || ""}
                                    onChange={handleFormChange}
                                  />
                                </label>
                                <label>
                                  {t("proposeForm.role")}
                                  <input
                                    name="role"
                                    type="text"
                                    placeholder={t("proposeForm.rolePlaceholder")}
                                    value={activeFormValues.role || ""}
                                    onChange={handleFormChange}
                                  />
                                </label>
                              </div>
                              <label>
                                {t("proposeForm.problemToSolve")}
                                <textarea
                                  name="problemToSolve"
                                  rows="3"
                                  placeholder={t("proposeForm.problemToSolvePlaceholder")}
                                  value={activeFormValues.problemToSolve || ""}
                                  onChange={handleFormChange}
                                />
                              </label>
                              <label>
                                {t("proposeForm.desiredCapabilities")}
                                <textarea
                                  name="desiredCapabilities"
                                  rows="3"
                                  placeholder={t("proposeForm.desiredCapabilitiesPlaceholder")}
                                  value={activeFormValues.desiredCapabilities || ""}
                                  onChange={handleFormChange}
                                />
                              </label>
                              <label>
                                {t("proposeForm.dataSources")}
                                <textarea
                                  name="dataSources"
                                  rows="3"
                                  placeholder={t("proposeForm.dataSourcesPlaceholder")}
                                  value={activeFormValues.dataSources || ""}
                                  onChange={handleFormChange}
                                />
                              </label>
                              <div className="form-grid">
                                <label>
                                  {t("proposeForm.integrations")}
                                  <input
                                    name="integrations"
                                    type="text"
                                    placeholder={t("proposeForm.integrationsPlaceholder")}
                                    value={activeFormValues.integrations || ""}
                                    onChange={handleFormChange}
                                  />
                                </label>
                                <label>
                                  {t("proposeForm.successMetrics")}
                                  <input
                                    name="successMetrics"
                                    type="text"
                                    placeholder={t("proposeForm.successMetricsPlaceholder")}
                                    value={activeFormValues.successMetrics || ""}
                                    onChange={handleFormChange}
                                  />
                                </label>
                                <label>
                                  {t("proposeForm.timeline")}
                                  <input
                                    name="timeline"
                                    type="text"
                                    placeholder={t("proposeForm.timelinePlaceholder")}
                                    value={activeFormValues.timeline || ""}
                                    onChange={handleFormChange}
                                  />
                                </label>
                                <label>
                                  {t("proposeForm.budgetRange")}
                                  <input
                                    name="budgetRange"
                                    type="text"
                                    placeholder={t("proposeForm.budgetRangePlaceholder")}
                                    value={activeFormValues.budgetRange || ""}
                                    onChange={handleFormChange}
                                  />
                                </label>
                              </div>
                              <label>
                                {t("proposeForm.additionalNotes")}
                                <textarea
                                  name="additionalNotes"
                                  rows="3"
                                  placeholder={t("proposeForm.additionalNotesPlaceholder")}
                                  value={activeFormValues.additionalNotes || ""}
                                  onChange={handleFormChange}
                                />
                              </label>
                            </>
                          ) : (
                            <>
                              <div className="form-grid">
                                <label>
                                  {t("existingForm.contactName")}
                                  <input
                                    name="contactName"
                                    type="text"
                                    placeholder={t("existingForm.contactNamePlaceholder")}
                                    value={activeFormValues.contactName || ""}
                                    onChange={handleFormChange}
                                    aria-required="true"
                                    className={validationErrors.contactName ? "input-error" : ""}
                                  />
                                  {validationErrors.contactName && (
                                    <span className="error-text">{validationErrors.contactName}</span>
                                  )}
                                </label>
                                <label>
                                  {t("existingForm.contactEmail")}
                                  <input
                                    name="contactEmail"
                                    type="email"
                                    placeholder={t("existingForm.contactEmailPlaceholder")}
                                    value={activeFormValues.contactEmail || ""}
                                    onChange={handleFormChange}
                                    aria-required="true"
                                    className={validationErrors.contactEmail ? "input-error" : ""}
                                  />
                                  {validationErrors.contactEmail && (
                                    <span className="error-text">{validationErrors.contactEmail}</span>
                                  )}
                                </label>
                                <label>
                                  {t("existingForm.company")}
                                  <input
                                    name="company"
                                    type="text"
                                    placeholder={t("existingForm.companyPlaceholder")}
                                    value={activeFormValues.company || ""}
                                    onChange={handleFormChange}
                                  />
                                </label>
                                <label>
                                  {t("existingForm.role")}
                                  <input
                                    name="role"
                                    type="text"
                                    placeholder={t("existingForm.rolePlaceholder")}
                                    value={activeFormValues.role || ""}
                                    onChange={handleFormChange}
                                  />
                                </label>
                                <label>
                                  {t("existingForm.useCase")}
                                  <input
                                    name="useCase"
                                    type="text"
                                    placeholder={t("existingForm.useCasePlaceholder")}
                                    value={activeFormValues.useCase || ""}
                                    onChange={handleFormChange}
                                  />
                                </label>
                                <label>
                                  {t("existingForm.keyWorkflows")}
                                  <input
                                    name="keyWorkflows"
                                    type="text"
                                    placeholder={t("existingForm.keyWorkflowsPlaceholder")}
                                    value={activeFormValues.keyWorkflows || ""}
                                    onChange={handleFormChange}
                                  />
                                </label>
                              </div>
                              <label>
                                {t("existingForm.dataSources")}
                                <textarea
                                  name="dataSources"
                                  rows="3"
                                  placeholder={t("existingForm.dataSourcesPlaceholder")}
                                  value={activeFormValues.dataSources || ""}
                                  onChange={handleFormChange}
                                />
                              </label>
                              <label>
                                {t("existingForm.integrations")}
                                <textarea
                                  name="integrations"
                                  rows="3"
                                  placeholder={t("existingForm.integrationsPlaceholder")}
                                  value={activeFormValues.integrations || ""}
                                  onChange={handleFormChange}
                                />
                              </label>
                              <div className="form-grid">
                                <label>
                                  {t("existingForm.expectedVolume")}
                                  <input
                                    name="expectedVolume"
                                    type="text"
                                    placeholder={t("existingForm.expectedVolumePlaceholder")}
                                    value={activeFormValues.expectedVolume || ""}
                                    onChange={handleFormChange}
                                  />
                                </label>
                                <label>
                                  {t("existingForm.timeline")}
                                  <input
                                    name="timeline"
                                    type="text"
                                    placeholder={t("existingForm.timelinePlaceholder")}
                                    value={activeFormValues.timeline || ""}
                                    onChange={handleFormChange}
                                  />
                                </label>
                                <label>
                                  {t("existingForm.budgetRange")}
                                  <input
                                    name="budgetRange"
                                    type="text"
                                    placeholder={t("existingForm.budgetRangePlaceholder")}
                                    value={activeFormValues.budgetRange || ""}
                                    onChange={handleFormChange}
                                  />
                                </label>
                                <label>
                                  {t("existingForm.preferredContact")}
                                  <input
                                    name="preferredContact"
                                    type="text"
                                    placeholder={t("existingForm.preferredContactPlaceholder")}
                                    value={activeFormValues.preferredContact || ""}
                                    onChange={handleFormChange}
                                  />
                                </label>
                              </div>
                              <label>
                                {t("existingForm.additionalNotes")}
                                <textarea
                                  name="additionalNotes"
                                  rows="3"
                                  placeholder={t("existingForm.additionalNotesPlaceholder")}
                                  value={activeFormValues.additionalNotes || ""}
                                  onChange={handleFormChange}
                                />
                              </label>
                            </>
                          )}

                          <div className="form-buttons">
                            <button
                              type="submit"
                              className="agent-order-submit"
                              disabled={isSubmitting}
                              aria-busy={isSubmitting}
                            >
                              {isSubmitting ? t("order.submitting") : t("order.submit")}
                            </button>
                            <button
                              type="button"
                              className="agent-order-submit agent-order-clear"
                              onClick={handleFormClear}
                              disabled={isSubmitting}
                            >
                              {t("order.clearForm")}
                            </button>
                          </div>
                        </form>
                      </div>
                    )}
                  </div>
                  </div>
                </section>

                  </div>

                  {/* FAQ Section */}
                  <section className="faq-section faq-section-fullbleed">
                    <div className="faq-section-inner">
                      <h2>{t("faq.sectionTitle")}</h2>
                      <div className="faq-list">
                        {faqItems.map((item, index) => (
                          <div
                            key={index}
                            className={`faq-item ${expandedFaqs[index] ? "expanded" : ""}`}
                          >
                            <button
                              type="button"
                              className="faq-question"
                              onClick={() => toggleFaq(index)}
                              aria-expanded={expandedFaqs[index] || false}
                            >
                              <span>{item.question}</span>
                              <span className="faq-icon">{expandedFaqs[index] ? "−" : "+"}</span>
                            </button>
                            {expandedFaqs[index] && (
                              <div 
                                className="faq-answer"
                                dangerouslySetInnerHTML={{ __html: item.answer }}
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </section>

                  <footer className="footer footer-fullbleed">
                    <div className="footer-inner">
                      <div className="footer-links">
                        <a
                          className="footer-link"
                          href="https://www.linkedin.com/in/fabianapiccolo/"
                          target="_blank"
                          rel="noreferrer"
                          aria-label="CodeCat on LinkedIn"
                        >
                          <img
                            src="/assets/logos/LinkedIn_icon.png"
                            alt="LinkedIn"
                            className="footer-social-logo"
                          />
                        </a>
                        <a
                          className="footer-link"
                          href="https://www.instagram.com/theaicodecat/"
                          target="_blank"
                          rel="noreferrer"
                          aria-label="CodeCat on Instagram"
                        >
                          <img
                            src="/assets/logos/Instagram-icon-Logo-2016-present.png"
                            alt="Instagram"
                            className="footer-social-logo"
                          />
                        </a>
                        <a
                          className="footer-link"
                          href="https://www.facebook.com/profile.php?id=61578039639625"
                          target="_blank"
                          rel="noreferrer"
                          aria-label="CodeCat on Facebook"
                        >
                          <img
                            src="/assets/logos/Facebook_logo.png"
                            alt="Facebook"
                            className="footer-social-logo"
                          />
                        </a>
                      </div>
                      <div>{t("footer.copyright")}</div>
                    </div>
                  </footer>
                </div>
            }
          />
        </Routes>
      </Router>
    </ThemeContext.Provider>
    </ErrorBoundary>
  );
}

function App() {
  return (
    <LanguageProvider>
      <AppContent />
    </LanguageProvider>
  );
}

export default App;